import React, { useState } from 'react';

import { Button, Input } from '@meu-mapa-nev/design-system';

export interface AdesaoDispositivoProps {
  readonly onConfirm: (deviceName: string) => void;
  readonly onSkip: () => void;
  readonly submitting?: boolean;
}

/**
 * Oferta de tornar este aparelho confiável, após o segundo passo.
 *
 * Três detalhes que não são estéticos:
 *
 * 1. a caixa começa DESMARCADA. Adesão é opt-in explícito, nunca automática —
 *    marcar por padrão criaria uma exceção permanente ao segundo fator sem
 *    que a pessoa tivesse decidido nada;
 * 2. o nome do aparelho é obrigatório. Sem nome, a lista de dispositivos vira
 *    um monte de linhas indistinguíveis e ninguém consegue revogar a certa;
 * 3. o prazo de 30 dias é dito na tela, e não escondido na documentação.
 */
export const AdesaoDispositivo: React.FC<AdesaoDispositivoProps> = ({
  onConfirm,
  onSkip,
  submitting = false,
}) => {
  const [aderir, setAderir] = useState(false);
  const [nome, setNome] = useState('');
  const [erro, setErro] = useState<string | undefined>(undefined);

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();

        if (!aderir) {
          onSkip();
          return;
        }

        if (nome.trim().length === 0) {
          setErro('Dê um nome a este aparelho para reconhecê-lo depois.');
          return;
        }

        setErro(undefined);
        onConfirm(nome.trim());
      }}
      style={{
        border: '1px solid var(--nev-border-subtle)',
        borderRadius: 'var(--nev-radius-compact)',
        padding: 'var(--nev-space-4)',
        backgroundColor: 'var(--nev-background-surface)',
      }}
    >
      <h2
        style={{
          fontFamily: 'var(--nev-font-heading)',
          fontSize: '1rem',
          fontWeight: 600,
          color: 'var(--nev-text-primary)',
          marginBottom: 'var(--nev-space-3)',
        }}
      >
        Confiar neste aparelho?
      </h2>

      <div style={{ display: 'flex', gap: 'var(--nev-space-2)', alignItems: 'flex-start' }}>
        <input
          id="aderir-dispositivo"
          type="checkbox"
          checked={aderir}
          disabled={submitting}
          onChange={(event) => {
            setAderir(event.target.checked);
          }}
          style={{ width: '24px', height: '24px', marginTop: '2px', flexShrink: 0 }}
        />
        <label
          htmlFor="aderir-dispositivo"
          style={{
            fontFamily: 'var(--nev-font-body)',
            fontSize: '0.9375rem',
            lineHeight: 1.5,
            color: 'var(--nev-text-primary)',
          }}
        >
          Não pedir o código de confirmação neste aparelho pelos próximos 30 dias. Use apenas em
          aparelhos pessoais, e revogue se perdê-lo.
        </label>
      </div>

      {aderir && (
        <div style={{ marginTop: 'var(--nev-space-4)' }}>
          <Input
            label="Nome deste aparelho"
            value={nome}
            required
            disabled={submitting}
            error={erro}
            helperText="Por exemplo: notebook do consultório."
            onChange={(event) => {
              setNome(event.target.value);
            }}
          />
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--nev-space-3)', marginTop: 'var(--nev-space-4)' }}>
        <Button type="submit" variant="primary" disabled={submitting}>
          Continuar
        </Button>
      </div>
    </form>
  );
};
